import { Cell } from './Types'
import i18n from './i18n'
import { Language } from './Enums/Language'

const LANGUAGE_LOCALES: Record<Language, string> = {
  [Language.ENGLISH]: 'en-US',
  [Language.DANISH]: 'da-DK',
  [Language.GERMAN]: 'de-DE',
}

export class SpreadsheetFormulaError {
  cellId: string
  message: string

  constructor(cellId: string, message: string) {
    this.cellId = cellId
    this.message = message
  }
}

export default class SpreadsheetFormulaCalculator {
  data: Map<string, Cell>
  errors: Map<string, string>

  constructor(data: Map<string, Cell>) {
    this.data = data
    this.errors = new Map()
  }

  static isFormula = (content: string): boolean => content[0] === '='

  static colToIndex = (col: string): number => {
    if (col.length === 1) return col.charCodeAt(0) - 97
    const lastChar = col[col.length - 1]
    const prefix = col.substring(0, col.length - 1)
    return (SpreadsheetFormulaCalculator.colToIndex(prefix) + 1) * 26 + (lastChar.charCodeAt(0) - 97)
  }

  static indexToCol = (index: number): string => {
    const prefixLetterNumber = Math.floor(index / 26)
    const letterIndex = index - prefixLetterNumber * 26
    const code = String.fromCharCode(97 + letterIndex)
    return prefixLetterNumber > 0
      ? `${SpreadsheetFormulaCalculator.indexToCol(prefixLetterNumber - 1)}${code}`
      : code
  }

  static expandRange = (startCell: string, endCell: string): string[] => {
    const parseCell = (cellId: string) => {
      const match = cellId.match(/^([a-zA-Z]+)([0-9]+)$/)
      if (!match) throw new Error(`Ugyldig cellereference: ${cellId}`)
      return { col: match[1].toLowerCase(), row: parseInt(match[2]) }
    }

    const start = parseCell(startCell)
    const end = parseCell(endCell)
    const minCol = Math.min(SpreadsheetFormulaCalculator.colToIndex(start.col), SpreadsheetFormulaCalculator.colToIndex(end.col))
    const maxCol = Math.max(SpreadsheetFormulaCalculator.colToIndex(start.col), SpreadsheetFormulaCalculator.colToIndex(end.col))
    const minRow = Math.min(start.row, end.row)
    const maxRow = Math.max(start.row, end.row)

    const cells: string[] = []
    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        cells.push(`${SpreadsheetFormulaCalculator.indexToCol(col)}${row}`)
      }
    }
    return cells
  }

  // Finds the first (leftmost) call to `name(...)` in `text` and returns its
  // inner content plus the position of the whole call, using paren-depth
  // counting so calls containing their own parens (e.g. a nested SUM range
  // or grouped math) are matched correctly rather than stopping at the
  // first `)`.
  static extractFunctionCall = (
    text: string,
    name: string,
  ): { inner: string; start: number; end: number } | null => {
    const openMatch = new RegExp(`${name}\\(`, 'i').exec(text)
    if (!openMatch) return null

    const start = openMatch.index
    const openParenIndex = start + openMatch[0].length - 1

    let depth = 0
    let closeParenIndex = -1
    for (let i = openParenIndex; i < text.length; i++) {
      if (text[i] === '(') depth++
      else if (text[i] === ')') {
        depth--
        if (depth === 0) {
          closeParenIndex = i
          break
        }
      }
    }

    if (closeParenIndex === -1) {
      throw new Error('Fejl i formel')
    }

    return {
      inner: text.substring(openParenIndex + 1, closeParenIndex),
      start,
      end: closeParenIndex + 1,
    }
  }

  // Groups referenced cells by the reference expression they came from — every
  // cell in a SUM(XX:YY) range is one group (so callers can color them as a
  // unit), while each direct cell reference outside a SUM gets its own group.
  static getReferenceGroups = (content: string): string[][] => {
    if (!SpreadsheetFormulaCalculator.isFormula(content)) return []

    const groups: string[][] = []

    const sumMatches = content.matchAll(/(?:SUM|AVG)\(([a-zA-Z]+[0-9]+):([a-zA-Z]+[0-9]+)\)/gi)
    for (const match of sumMatches) {
      groups.push(
        SpreadsheetFormulaCalculator.expandRange(match[1], match[2]).map((c) =>
          c.toLowerCase(),
        ),
      )
    }

    // Direct cell references (outside of SUM/AVG expressions)
    const withoutSums = content.replace(/(?:SUM|AVG)\([^)]+\)/gi, '')
    const directMatches = withoutSums.match(/([a-zA-Z]+[0-9]+)/g) || []
    directMatches.forEach((cellId) => groups.push([cellId.toLowerCase()]))

    return groups
  }

  static getReferencedCells = (content: string): string[] =>
    SpreadsheetFormulaCalculator.getReferenceGroups(content).flat()

  // The decimal separator to display/parse computed values with, based on the
  // user's interface language (e.g. English -> '.', Danish/German -> ',').
  static getDecimalSeparator = (): string => {
    const locale = LANGUAGE_LOCALES[i18n.language as Language] ?? LANGUAGE_LOCALES[Language.DANISH]
    return new Intl.NumberFormat(locale).format(1.1).charAt(1)
  }

  // Converts a value/expression written with the current locale's decimal
  // separator into a '.'-decimal string that parseFloat/eval understand.
  static toParsableExpression = (value: string): string => {
    const separator = SpreadsheetFormulaCalculator.getDecimalSeparator()
    return separator === '.' ? value : value.split(separator).join('.')
  }

  // Computed cell values are always stored in canonical '.'-decimal form (so
  // they stay parseable regardless of the language active when they were
  // computed); this converts one to the current locale's decimal separator
  // purely for display, so switching language reformats immediately without
  // needing to recompute/re-save every cell.
  static formatForDisplay = (value: string): string =>
    Number.isFinite(Number(value))
      ? value.replace('.', SpreadsheetFormulaCalculator.getDecimalSeparator())
      : value

  rebuildSpreadsheet = (cellId: string, content: string, iteration: number) => {
    let value: string

    try {
      if (iteration > 1000) {
        throw new SpreadsheetFormulaError(cellId, 'Cirkulær reference')
      }

      value = SpreadsheetFormulaCalculator.isFormula(content)
        ? this.evaluateCellFormula(content)
        : content
    } catch (error) {
      value = content
      if (error instanceof SpreadsheetFormulaError || error instanceof Error) {
        console.error(`[Spreadsheet] Error evaluating cell ${cellId} ("${content}"):`, error)
        this.errors.set(cellId, error.message)
      } else {
        throw error
      }
    }

    const existingCell = this.data.get(cellId)

    this.data.set(cellId.toLowerCase(), {
      content,
      value,
      styling: existingCell ? existingCell.styling : [],
      type: existingCell?.type,
    })

    // Iterate all existing data to recalculate formula that reference this updated field.
    if (!this.errors.get(cellId)) {
      this.data.forEach((cell, key) => {
        // Check if the cell is a formula and if the cell contains the current cellId
        // If so, it should be updated with the new value of this cell
        if (
          SpreadsheetFormulaCalculator.isFormula(cell.content) &&
          cell.content.indexOf(cellId) > -1
        ) {
          this.rebuildSpreadsheet(key, cell.content, iteration + 1)
        }
      })
    }
  }

  evaluateCellFormula = (formula: string): string =>
    this.evaluateExpression(formula.substring(1))

  evaluateExpression = (expression: string): string => {
    let value = expression

    // Handle SUM(XX:YY) — resolve range to a number before further evaluation
    value = value.replace(
      /SUM\(([a-zA-Z]+[0-9]+):([a-zA-Z]+[0-9]+)\)/gi,
      (_, startCell, endCell) => {
        const cells = SpreadsheetFormulaCalculator.expandRange(startCell, endCell)
        let sum = 0
        for (const cellId of cells) {
          const cell = this.data.get(cellId.toLowerCase())
          if (cell && cell.value) {
            const num = parseFloat(SpreadsheetFormulaCalculator.toParsableExpression(cell.value))
            if (!isNaN(num)) sum += num
          }
        }
        return parseFloat(sum.toPrecision(10)).toString()
      },
    )

    // Handle AVG(XX:YY) — resolve range to a number before further evaluation
    value = value.replace(
      /AVG\(([a-zA-Z]+[0-9]+):([a-zA-Z]+[0-9]+)\)/gi,
      (_, startCell, endCell) => {
        const cells = SpreadsheetFormulaCalculator.expandRange(startCell, endCell)
        let sum = 0
        let count = 0
        for (const cellId of cells) {
          const cell = this.data.get(cellId.toLowerCase())
          if (cell && cell.value) {
            const num = parseFloat(SpreadsheetFormulaCalculator.toParsableExpression(cell.value))
            if (!isNaN(num)) {
              sum += num
              count++
            }
          }
        }
        const avg = count > 0 ? sum / count : 0
        return parseFloat(avg.toPrecision(10)).toString()
      },
    )

    // Handle ROUND(...) — the inner content may itself be a math expression,
    // cell reference, or another function call (e.g. SUM), so it's evaluated
    // recursively before rounding to 2 decimals.
    let roundCall = SpreadsheetFormulaCalculator.extractFunctionCall(value, 'ROUND')
    while (roundCall) {
      const inner = this.evaluateExpression(roundCall.inner)
      const num = parseFloat(SpreadsheetFormulaCalculator.toParsableExpression(inner))

      if (isNaN(num)) {
        throw new Error('Fejl i formel')
      }

      const rounded = (Math.round((num + Number.EPSILON) * 100) / 100).toString()

      value =
        value.substring(0, roundCall.start) +
        rounded +
        value.substring(roundCall.end)

      roundCall = SpreadsheetFormulaCalculator.extractFunctionCall(value, 'ROUND')
    }

    const hasValidChars = /^[0-9+\-*/().,\sA-Za-z]+$/.test(value)
    const hasOperator = /[+\-*/]/.test(value)

    const isMathExpression = hasValidChars && hasOperator

    // Look for references to other cells
    const matches = value.match(/([a-zA-Z]+[0-9]+)/g)

    if (matches) {
      matches.forEach((match: string) => {
        const matchedCell = this.data.get(match.toLowerCase())

        // If the matched cell contains a formula, then it suggests that theres a cirkular reference
        if (
          matchedCell &&
          SpreadsheetFormulaCalculator.isFormula(matchedCell.value)
        ) {
          throw new SpreadsheetFormulaError(match, 'Cirkulær reference')
        }

        if (matchedCell) {
          value = value.replace(match, matchedCell.value)
        } else {
          value = value.replace(match, '')
        }

        if (!value && isMathExpression) {
          throw new SpreadsheetFormulaError(match, 'Feltet er tomt')
        }
      })
    }

    if (isMathExpression) {
      try {
        const result = eval(SpreadsheetFormulaCalculator.toParsableExpression(value))
        value = parseFloat(result.toPrecision(10)).toString()
      } catch (error) {
        console.error(`[Spreadsheet] eval() failed for expression "${value}":`, error)
        throw new Error('Fejl i formel')
      }

      if (!Number.isFinite(parseFloat(value))) {
        throw new Error('Division med 0')
      }
    }

    return value
  }

  getData() {
    return this.data
  }
}
